"use client";

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Image from "next/image";
import dynamic from "next/dynamic";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { cn } from "@/lib/utils";
import type { PageAiFeaturesSchema } from "@/sanity/pages/PageAiFeaturesTemplate";
import { SectionHeading } from "./ui";
import InteractiveCoach from "./InteractiveCoach";
import { COACH_COLORS } from "./coach-colors";
import styles from "./ai-features.module.scss";

const FOXES = ["fox-4", "fox-5", "fox-1", "fox-2"] as const;
// Duplicate the four shapes so edge recycling stays outside the visible slots.
const RING_SIZE = FOXES.length * 2;
const RING_STOPS = [
  { x: 0, scale: 1, opacity: 0 },
  { x: 99.333333, scale: 2 / 3, opacity: 0.35 },
  { x: 175.333333, scale: 8 / 15, opacity: 0.25 },
  { x: 251.2, scale: 0.45, opacity: 0 },
];
const CoachAura = dynamic(() => import("./CoachAura"), { ssr: false });

export default function CoachPickerSection({
  data,
}: {
  data?: PageAiFeaturesSchema["personalize"];
}) {
  const [fox, setFox] = useState(2);
  const move = useCallback((step: number) => {
    if (step) setFox((value) => value + step);
  }, []);
  const previousSlots = useRef<number[]>([]);
  const [color, setColor] = useState(0);
  const [name, setName] = useState("");
  const ref = useRef<HTMLElement>(null);
  const activity = useRef(0);
  const inView = useInView(ref, { amount: 0.25 });
  const reduced = !!useReducedMotion();
  const [visible, setVisible] = useState(true);
  const palette = COACH_COLORS[color];
  const interact = () => {
    activity.current = performance.now();
  };
  useEffect(() => {
    const update = () => setVisible(!document.hidden);
    update();
    document.addEventListener("visibilitychange", update);
    return () => document.removeEventListener("visibilitychange", update);
  }, []);
  useEffect(() => {
    if (!inView || !visible || reduced) return;
    activity.current = performance.now();
    let step = 0;
    const timer = window.setInterval(() => {
      const focused = document.activeElement;
      if (
        performance.now() - activity.current < 9000 ||
        (focused instanceof HTMLElement &&
          ref.current?.contains(focused) &&
          focused.matches(
            'input[type="text"],input:not([type]),:focus-visible',
          ))
      )
        return;
      step++;
      if (step % 3 === 0) move(1);
      else setColor((value) => (value + 1) % COACH_COLORS.length);
    }, 2600);
    return () => window.clearInterval(timer);
  }, [inView, visible, reduced, move]);
  const coachName = name.trim() || "TS Coach";
  const variant = FOXES[((fox % FOXES.length) + FOXES.length) % FOXES.length];
  const slots = Array.from(
    { length: RING_SIZE },
    (_, index) =>
      ((((index - fox) % RING_SIZE) + RING_SIZE * 1.5) % RING_SIZE) -
      RING_SIZE / 2,
  );
  const duration = reduced || !inView || !visible ? 0 : 0.6;
  useEffect(() => {
    previousSlots.current = slots;
  }, [slots]);
  if (!data) return null;
  return (
    <section
      ref={ref}
      id="personalize"
      className={styles.personalize}
      data-coach-color={color}
      data-coach-idle={inView && visible && !reduced}
      style={
        {
          "--coach-top": palette.top,
          "--coach-bottom": palette.bottom,
          "--coach-accent": palette.accent,
        } as CSSProperties
      }
      onPointerMoveCapture={interact}
      onPointerDownCapture={interact}
      onKeyDownCapture={interact}
      onFocusCapture={interact}
    >
      <div className="px-[3rem]">
        <SectionHeading data={data} />
      </div>
      <div className={styles.foxBand} data-color={color}>
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Previous coach"
          onClick={() => move(-1)}
        >
          <ChevronLeft />
        </button>
        <div className={styles.foxTrack}>
          <span className={styles.foxStage} data-coach-stage aria-hidden="true">
            <span
              className={styles.foxColor}
              data-coach-backdrop
              data-color={color}
            >
              {inView && (
                <CoachAura
                  color={color}
                  variant={variant}
                  active={visible}
                  reduced={reduced}
                />
              )}
            </span>
            <span className={styles.foxTransition}>
              <InteractiveCoach variant={variant} reaction={fox} />
            </span>
          </span>
          {slots.map((offset, index) => {
            const distance = Math.abs(offset);
            const {
              x: magnitude,
              scale,
              opacity,
            } = RING_STOPS[Math.min(distance, 3)];
            const x = Math.sign(offset) * magnitude;
            const recycled =
              Math.abs(offset - (previousSlots.current[index] ?? offset)) >
              RING_SIZE / 2;
            return (
              <motion.button
                key={index}
                type="button"
                aria-label={
                  offset === 0
                    ? `Coach ${(index % FOXES.length) + 1}: ${coachName}`
                    : `Coach ${(index % FOXES.length) + 1}`
                }
                aria-pressed={offset === 0}
                aria-hidden={distance > 2}
                tabIndex={distance > 2 ? -1 : 0}
                data-coach-variant={FOXES[index % FOXES.length]}
                data-coach-slot={offset}
                onClick={() => move(offset)}
                className={styles.fox}
                initial={false}
                animate={{
                  x:
                    recycled && distance <= 2
                      ? [`${Math.sign(offset) * 251.2}%`, `${x}%`]
                      : `${x}%`,
                  scale: recycled && distance <= 2 ? [0.45, scale] : scale,
                  opacity: recycled && distance <= 2 ? [0, opacity] : opacity,
                }}
                transition={{
                  duration: recycled && distance > 2 ? 0 : duration,
                  ease: [0.65, 0, 0.35, 1],
                }}
                style={{ pointerEvents: distance > 2 ? "none" : "auto" }}
              >
                <Image
                  src={`/ai-features/coach-faces/${FOXES[index % FOXES.length]}.svg`}
                  width={210}
                  height={210}
                  alt=""
                  className={styles.foxDrawing}
                />
              </motion.button>
            );
          })}
        </div>
        <button
          type="button"
          className={styles.iconButton}
          aria-label="Next coach"
          onClick={() => move(1)}
        >
          <ChevronRight />
        </button>
        <p className={styles.coachIdentity} title={coachName}>
          {coachName}
        </p>
      </div>
      <div className={styles.pickerControls}>
        <label className={styles.nameField} htmlFor="ai-coach-name">
          <span>{data.nameLabel || "Coach name"}</span>
          <input
            id="ai-coach-name"
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={data.namePlaceholder}
            maxLength={40}
            autoComplete="off"
          />
        </label>
        <fieldset>
          <legend>{data.colorsLabel}</legend>
          <div className={styles.swatches}>
            {COACH_COLORS.map(({ name: label }, i) => (
              <label
                key={label}
                className={cn(
                  styles.swatch,
                  color === i && styles.activeSwatch,
                )}
              >
                <input
                  className="sr-only"
                  type="radio"
                  name="ai-coach-color"
                  value={i}
                  checked={color === i}
                  onChange={() => setColor(i)}
                />
                <span className={styles.swatchArt}>
                  <Image
                    src={`/ai-features/color-${i + 1}.svg`}
                    width={125}
                    height={125}
                    alt=""
                  />
                </span>
                <span>{label}</span>
              </label>
            ))}
          </div>
        </fieldset>
      </div>
    </section>
  );
}
